import { RowSelectionState } from '@tanstack/react-table';

import { EditedEntryValues } from '../types/approvals-table.types';
import {
  ApprovalEditedEntries,
  ApprovalEntryExclusion,
  ApprovalExclusion,
  ApprovalExclusionKind,
  ApprovalProjectRow,
  ApprovalSelectedMember,
  ApprovalSelection,
  ApprovalSelectionLevel,
  ApprovalTimeEntry,
  TimeEntryStatus,
} from '../types/time-tracking-approvals';
import { getApprovalMemberRowId } from './approval-row-model.helper';
import { mergeEditedEntryValues } from './merge-edited-entry.helper';

/**
 * Resolves the selection level a row id belongs to. Row ids encode the row kind:
 * `entry-{id}` → worklog, `project-{pid}-member-{mid}` → member, `project-{id}` → project
 * (see `getApprovalRowId`). Time-off rows (`timeoff-…`) are never selectable.
 */
export const getSelectionLevelForRowId = (rowId: string): ApprovalSelectionLevel => {
  if (rowId.startsWith('entry-')) return ApprovalSelectionLevel.Worklogs;
  if (rowId.includes('-member-')) return ApprovalSelectionLevel.Members;
  return ApprovalSelectionLevel.Projects;
};

/** Exclusions recorded under a given selected ancestor row. */
const getExclusionsForAncestor = (
  exclusions: Record<string, ApprovalExclusion>,
  ancestorRowId: string
): ApprovalExclusion[] =>
  Object.values(exclusions).filter(exclusion => exclusion.ancestorRowId === ancestorRowId);

/** Entry exclusions of a list, narrowed. */
const getEntryExclusions = (exclusions: ApprovalExclusion[]): ApprovalEntryExclusion[] =>
  exclusions.filter(
    (exclusion): exclusion is ApprovalEntryExclusion =>
      exclusion.kind === ApprovalExclusionKind.Entry
  );

interface CollectEditedEntriesParams {
  /** Every row with a pending edit, as it looked when first edited. */
  editedEntrySnapshots: Record<number, ApprovalTimeEntry>;
  editedEntryValues: Record<number, EditedEntryValues>;
  /** True when the snapshot sits underneath the selected aggregate row. */
  covers: (entry: ApprovalTimeEntry) => boolean;
  /** Worklog ids the user unchecked underneath the selection. */
  excludedEntryIds: Set<number>;
  /** Member row ids the user unchecked underneath a selected project. */
  excludedMemberRowIds?: Set<string>;
}

/**
 * Worklog entries that carry a pending inline edit AND that an approve on this selection would
 * actually cover: underneath it, still pending, not excluded, not under an excluded member.
 *
 * Resolved from the STORE'S SNAPSHOTS, never from the loaded tree. Collapsing a section drops
 * its rows, and walking the tree therefore found nothing — so an aggregate approve over a
 * collapsed section sent no worklog items and silently approved every edited row at its old
 * tracked hours. The snapshot carries the id, `hash` and ancestors a request item needs, so the
 * edit survives the row unmounting.
 */
const collectEditedEntries = ({
  editedEntrySnapshots,
  editedEntryValues,
  covers,
  excludedEntryIds,
  excludedMemberRowIds,
}: CollectEditedEntriesParams): ApprovalEditedEntries =>
  Object.values(editedEntrySnapshots)
    .filter(entry => {
      if (!covers(entry)) return false;
      // Only still-pending rows are approvable; an approved row is server-owned.
      if (entry.status !== TimeEntryStatus.SUBMITTED) return false;
      if (excludedEntryIds.has(entry.id)) return false;
      if (excludedMemberRowIds?.has(getApprovalMemberRowId(entry.projectId, entry.memberId))) {
        return false;
      }
      return editedEntryValues[entry.id] !== undefined;
    })
    // The snapshot's approved values are the pre-edit baseline the aggregate row already counts
    // for this entry — the counts need both it and the edited row to derive a per-side delta.
    .map(entry => ({
      entry: mergeEditedEntryValues(entry, editedEntryValues[entry.id]),
      baseline: { invoice: entry.approvedForInvoiceHours, bill: entry.approvedForBillHours },
    }));

interface DeriveApprovalSelectionParams {
  rowSelection: RowSelectionState;
  exclusions: Record<string, ApprovalExclusion>;
  /** The loaded project tree (sentinel-free). */
  projects: ApprovalProjectRow[];
  /** Worklog-level entries resolved by the table, with pending edits already merged. */
  selectedEntries: ApprovalTimeEntry[];
  /** Pending inline edits, keyed by entry id. */
  editedEntryValues: Record<number, EditedEntryValues>;
  /** Snapshots of the edited rows — see `collectEditedEntries`. */
  editedEntrySnapshots: Record<number, ApprovalTimeEntry>;
}

/**
 * Builds the discriminated selection payload from the selection store state and the loaded
 * project tree. Project/member rows resolve WITHOUT their children being loaded — only the
 * row itself (id + hash), its recorded exclusions, and the pending edits of its loaded
 * entries are needed. Worklog-level entries are resolved by the table (it owns the table's
 * selected row model), so the caller passes them in.
 */
export const deriveApprovalSelection = ({
  rowSelection,
  exclusions,
  projects,
  selectedEntries,
  editedEntryValues,
  editedEntrySnapshots,
}: DeriveApprovalSelectionParams): ApprovalSelection => {
  const selectedProjects = projects
    .filter(project => rowSelection[project.id])
    .map(project => {
      const projectExclusions = getExclusionsForAncestor(exclusions, project.id);
      return {
        project,
        exclusions: projectExclusions,
        editedEntries: collectEditedEntries({
          editedEntrySnapshots,
          editedEntryValues,
          covers: entry => entry.projectId === project.project.projectId,
          excludedEntryIds: new Set(
            getEntryExclusions(projectExclusions).map(exclusion => exclusion.entryId)
          ),
          excludedMemberRowIds: new Set(
            projectExclusions
              .filter(exclusion => exclusion.kind === ApprovalExclusionKind.Member)
              .map(exclusion => exclusion.rowId)
          ),
        }),
      };
    });
  if (selectedProjects.length > 0) {
    return { level: ApprovalSelectionLevel.Projects, projects: selectedProjects };
  }

  const selectedMembers: ApprovalSelectedMember[] = [];
  for (const project of projects) {
    for (const member of project.members) {
      if (rowSelection[member.id]) {
        // Under a member selection only entry exclusions can exist.
        const memberExclusions = getEntryExclusions(
          getExclusionsForAncestor(exclusions, member.id)
        );
        selectedMembers.push({
          member,
          projectId: project.project.projectId,
          projectName: project.project.projectName,
          exclusions: memberExclusions,
          editedEntries: collectEditedEntries({
            editedEntrySnapshots,
            editedEntryValues,
            covers: entry =>
              entry.projectId === project.project.projectId && entry.memberId === member.member.id,
            excludedEntryIds: new Set(memberExclusions.map(exclusion => exclusion.entryId)),
          }),
        });
      }
    }
  }
  if (selectedMembers.length > 0) {
    return { level: ApprovalSelectionLevel.Members, members: selectedMembers };
  }

  return { level: ApprovalSelectionLevel.Worklogs, entries: selectedEntries };
};

/**
 * Ids of the entries whose pending inline edits an approve on this selection commits.
 * Worklog level commits every selected entry; aggregate levels commit only the edited
 * entries they split out (see `mapApprovalSelectionToRequest`).
 */
export const getSelectionCommittedEntryIds = (selection: ApprovalSelection): number[] => {
  switch (selection.level) {
    case ApprovalSelectionLevel.Projects:
      return selection.projects.flatMap(({ editedEntries }) =>
        editedEntries.map(({ entry }) => entry.id)
      );
    case ApprovalSelectionLevel.Members:
      return selection.members.flatMap(({ editedEntries }) =>
        editedEntries.map(({ entry }) => entry.id)
      );
    case ApprovalSelectionLevel.Worklogs:
      return selection.entries.map(entry => entry.id);
  }
};
